import React, { useEffect, useState } from 'react';
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    List,
    ListItem,
    Typography,
    Backdrop,
    CircularProgress,
    Slide,
} from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import { ArrowBack, Close } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { getTLAttendance } from '../../store/actions/cmActionCreator';
import MonthSlider from '../UI/MonthSlider';

interface TLInfo {
    [key: string]: any;
}

interface TLAttendanceLineItem {
    [key: string]: any;
}

interface TLAttendance {
    attendanceResponseList: TLAttendanceLineItem[];
}

const statusMapping = {
    IN_POSSESION: 'IN POSSESSION',
    RETURNED: 'RETURNED',
} as const;

const useStyles = makeStyles((theme) => ({
    cardSubHeader: {
        fontSize: '22px',
        fontWeight: 700,
    },
    cardHeader: {
        fontSize: '16px',
        fontWeight: 600,
    },
    cardHeaderFail: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#FB8A06',
    },
    cardSubHeaderFail: {
        fontSize: '22px',
        fontWeight: 700,
        color: '#FB8A06',
    },
    cardHeaderActive: {
        fontWeight: 700,
    },
    verticalCardContentsNoMargin: {
        margin: 0,
    },
    iconButton: {
        padding: '0px',
    },
    modalTitle: {
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#14112d',
        marginLeft: '14px',
    },
    modalSubTitle: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#14112d',
        marginLeft: '14px',
    },
    dialogContent: {
        paddingLeft: 0,
        paddingRight: 0,
    },
    listItemHeader: {
        border: '0px',
        marginBottom: '8px',
        color: '#808080',
        fontWeight: 600,
        padding: 0,
        paddingLeft: 16,
        paddingRight: 22,
        paddingTop: 16,
        borderTop: '1px solid #EAEDF3',
        fontSize: '14px',
    },
    listItem: {
        padding: '6px 16px',
        border: '1px solid #ebedf8',
        borderRadius: 4,
        marginBottom: 8,
        '&:last-child': {
            marginBottom: 0,
        },
    },
    name: {
        fontSize: 16,
        fontWeight: 700,
        color: '#14112D',
    },
    IN_POSSESION: {
        backgroundColor: '#EBEBFF',
        borderRadius: 4,
        border: '1px solid #579B5A',
        padding: '0px 8px',
        marginTop: 4,
        color: '#579B5A',
        fontWeight: 600,
        fontSize: 12,
        textAlign: 'center',
        maxWidth: 110,
    },
    REJECTED: {
        backgroundColor: '#EBEBFF',
        borderRadius: 4,
        border: '1px solid #f44336',
        padding: '0px 8px',
        marginTop: 4,
        color: '#f44336',
        fontWeight: 600,
        fontSize: 12,
        textAlign: 'center',
        maxWidth: 110,
    },
    RETURNED: {
        backgroundColor: '#fff',
        borderRadius: 4,
        marginTop: 4,
        border: '1px solid #B9B9B9',
        padding: '0px 8px',
        color: '#828282',
        fontWeight: 600,
        fontSize: 12,
        textAlign: 'center',
        maxWidth: 91,
    },
    backDropRoot: {
        position: 'absolute',
        zIndex: theme.zIndex.drawer - 1,
        opacity: 0.5,
    },
}));

const Transition = React.forwardRef<
    unknown,
    TransitionProps & { children?: React.ReactElement<any, any> }
>(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface ViewAttendanceModalProps {
    tlInfo: TLInfo;
    open: boolean;
    onClose: () => void;
    defaultValue?: Record<string, any>;
    orgList?: any[];
    preferredWorkLocationsList?: any[];
    selected?: any;
}

const ViewAttendanceModal: React.FC<ViewAttendanceModalProps> = ({
    tlInfo,
    open,
    onClose,
    defaultValue = {},
    ...props
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(open);
    const [loading, setLoading] = useState(false);
    const [tlAttendance, setTLAttendance] = useState<TLAttendance>({
        attendanceResponseList: [],
    });
    const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());

    // Set min date to 1 year ago
    const minDate = moment().subtract(1, 'year').toDate();
    // Set max date to current month
    const maxDate = new Date();

    const handleClose = () => {
        setIsOpen(false);
        onClose?.();
    };

    const loadTLAttendance = () => {
        if (tlInfo?.id) {
            setLoading(true);
            dispatch(
                getTLAttendance(
                    tlInfo.id,
                    {
                        pageNo: 0,
                        pageSize: 32,
                        monthOfYear: moment(selectedMonth).month() + 1,
                        year: moment(selectedMonth).year(),
                    },
                    (data: any) => {
                        setLoading(false);
                        if (data?.status?.code === 200 && data?.response) {
                            setTLAttendance(data.response);
                        }
                    },
                    () => {
                        setLoading(false);
                    },
                ),
            );
        }
    };

    useEffect(() => {
        loadTLAttendance();
    }, []);

    useEffect(() => {
        loadTLAttendance();
    }, [selectedMonth]);
    useEffect(() => {
        setIsOpen(open);
    }, [open]);
    return (
        <Dialog
            onClose={handleClose}
            open={isOpen}
            disableBackdropClick
            TransitionComponent={Transition}
            aria-labelledby="tl-attendance-dialog-slide-title"
            aria-describedby="tl-attendance-dialog-slide-description"
            disableEscapeKeyDown
            fullWidth
            scroll="paper"
            maxWidth="lg"
        >
            <DialogTitle id="tl-attendance-dialog-slide-title">
                <Box
                    width="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Box width="45%" display="flex" alignItems="center">
                        <IconButton
                            aria-label="close"
                            className={classes.iconButton}
                            onClick={handleClose}
                        >
                            <ArrowBack style={{ color: '#000' }} />
                        </IconButton>
                        <Box>
                            <Typography
                                className={classes.modalTitle}
                                variant="h6"
                            >
                                {`${tlInfo?.name} Attendance`}
                            </Typography>
                            {tlInfo?.contactNumber && (
                                <Typography
                                    className={classes.modalSubTitle}
                                    variant="h6"
                                >
                                    {tlInfo?.contactNumber}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <IconButton
                        aria-label="close"
                        className={classes.iconButton}
                        onClick={handleClose}
                    >
                        <Close style={{ color: '#000' }} />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Box margin={1}>
                    <MonthSlider
                        value={selectedMonth}
                        onChange={setSelectedMonth}
                        minDate={minDate}
                        maxDate={maxDate}
                        format="MMM YYYY"
                    />
                </Box>
                <Box width="100%">
                    <List component="nav">
                        <ListItem className={classes.listItemHeader}>
                            <Box
                                display="flex"
                                width="100%"
                                alignItems="center"
                            >
                                <Box width="25%">Date</Box>
                                <Box width="25%">Login Time</Box>
                                <Box width="25%">LogOut Time</Box>
                                <Box width="25%">Total active hours</Box>
                                {/* <Box width="23%">Break hours</Box> */}
                            </Box>
                        </ListItem>
                        {tlAttendance?.attendanceResponseList?.map((val) => (
                            <ListItem
                                key={val.id}
                                button
                                className={classes.listItem}
                            >
                                <Box width="25%">
                                    <Typography className={classes.name}>
                                        {val?.attendanceDate || '--'}
                                    </Typography>
                                    <Typography className={classes.name}>
                                        {moment(val?.attendanceDate).format(
                                            'dddd',
                                        )}
                                    </Typography>
                                </Box>

                                <Box width="25%">
                                    <Typography className={classes.name}>
                                        {val?.signInTime
                                            ? moment(val?.signInTime).format(
                                                  'hh:mm A',
                                              )
                                            : '---'}
                                    </Typography>
                                </Box>
                                <Box width="25%">
                                    <Typography className={classes.name}>
                                        {val?.signOutTime
                                            ? moment(val?.signOutTime).format(
                                                  'hh:mm A',
                                              )
                                            : '---'}
                                    </Typography>
                                </Box>
                                <Box width="25%">
                                    <Typography className={classes.name}>
                                        {val?.activeTime
                                            ? val?.signOutTime
                                                ? moment(val?.signOutTime).diff(
                                                      moment(val?.signInTime),
                                                      'hour',
                                                  ) +
                                                  'hrs ' +
                                                  (moment(
                                                      val?.signOutTime,
                                                  ).diff(
                                                      moment(val?.signInTime),
                                                      'minute',
                                                  ) %
                                                      60) +
                                                  'mins'
                                                : moment().diff(
                                                      moment(val?.signInTime),
                                                      'hour',
                                                  ) +
                                                  'hrs ' +
                                                  (moment().diff(
                                                      moment(val?.signInTime),
                                                      'minute',
                                                  ) %
                                                      60) +
                                                  'mins'
                                            : '--'}
                                    </Typography>
                                </Box>
                                {/* <Box width="25%">
                                    <Typography className={classes.name}>
                                        &#8377;{val?.totalRentPaid || '0'}
                                    </Typography>
                                </Box> */}
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </DialogContent>
            <Backdrop
                className={classes.backDropRoot}
                open={loading}
                onClick={undefined}
            >
                <CircularProgress color="primary" />
            </Backdrop>
        </Dialog>
    );
};

export default ViewAttendanceModal;
